/**
 * RolloutPlanModal — 21-day album rollout plan generated by the LLM.
 *
 * Timeline: 9 steps anchored to a chosen release_date (default = today).
 * Each step gets a suggested platform, a caption, hashtags, and a Copy+Open
 * shortcut that hands the artist off to the platform's post composer.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import {
  Calendar, Sparkles, Copy, ExternalLink, RefreshCw, Loader2,
  Instagram, Facebook, Twitter, Music2,
} from "lucide-react";

const PLATFORM_META = {
  instagram: { label: "Instagram", icon: Instagram, url: () => "https://www.instagram.com/" },
  facebook:  { label: "Facebook",  icon: Facebook,  url: () => "https://www.facebook.com/" },
  x:         { label: "X",         icon: Twitter,
    url: (text) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text || "")}` },
  tiktok:    { label: "TikTok",    icon: Music2,    url: () => "https://www.tiktok.com/upload" },
};

function fmtDate(iso) {
  try {
    const d = new Date(iso + "T12:00:00Z");
    return d.toLocaleDateString([], { month: "short", day: "numeric", weekday: "short" });
  } catch { return iso; }
}

export default function RolloutPlanModal({ albumId, albumTitle, onClose }) {
  // Pre-fill with today; artist can change to the actual release date.
  const [releaseDate, setReleaseDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/social-kit/album/${albumId}/rollout`, { release_date: releaseDate });
      setPlan(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const copy = async (text) => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); toast.success("Caption copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  const copyOpen = async (step) => {
    const full = step.hashtags ? `${step.caption}\n\n${step.hashtags}` : step.caption;
    if (!full) return;
    try { await navigator.clipboard.writeText(full); } catch { /* proceed */ }
    const meta = PLATFORM_META[step.suggested_platform] || PLATFORM_META.instagram;
    window.open(meta.url(full), "_blank", "noopener,noreferrer");
    toast.success(`Copied — pasting into ${meta.label}`);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="rollout-plan-modal"
        className="rounded-none bg-[#0A0A0A] border-[#2A2A2A] max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Calendar className="h-5 w-5 text-[#FF4500]" />
            21-Day Rollout Plan · "{albumTitle}"
          </DialogTitle>
          <div className="font-mono text-[11px] text-gray-500 mt-1">
            Your AI marketing manager. A dated, ready-to-post plan from teaser to launch to follow-ups.
          </div>
        </DialogHeader>

        {/* Release date + regenerate row */}
        <div className="flex flex-wrap gap-2 items-end mt-3 pb-4 border-b border-[#2A2A2A]">
          <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
              Release date
            </label>
            <input
              data-testid="rollout-release-date"
              type="date"
              value={releaseDate}
              onChange={(e) => setReleaseDate(e.target.value)}
              className="bg-black border border-[#2A2A2A] px-3 h-10 font-mono text-sm focus:outline-none focus:border-[#FF4500]"
            />
          </div>
          <Button
            data-testid="rollout-regenerate"
            onClick={load}
            disabled={busy}
            className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-10"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-2" />}
            Regenerate
          </Button>
        </div>

        {busy && !plan && (
          <div className="py-12 text-center font-mono text-xs text-gray-400 uppercase tracking-widest">
            <Loader2 className="h-6 w-6 mx-auto mb-3 animate-spin text-[#FF4500]" />
            Planning your rollout...
          </div>
        )}

        {plan && (
          <ol className="mt-4 space-y-4">
            {plan.plan.map((step) => {
              const meta = PLATFORM_META[step.suggested_platform] || PLATFORM_META.instagram;
              const PIcon = meta.icon;
              const isMilestone = step.day_offset === 0;
              return (
                <li
                  key={step.id}
                  data-testid={`rollout-step-${step.id}`}
                  className={`border ${isMilestone ? "border-[#FF4500] bg-[#FF4500]/[0.05]" : "border-[#2A2A2A] bg-black/40"} p-4`}
                >
                  <div className="flex flex-wrap items-baseline gap-2 mb-2">
                    <div className="font-display text-lg">{fmtDate(step.date)}</div>
                    <div className={`font-mono text-[10px] uppercase tracking-widest ${isMilestone ? "text-[#FF4500]" : "text-gray-500"}`}>
                      Day {step.day_offset >= 0 ? "+" : ""}{step.day_offset}
                    </div>
                    <div className="ml-auto flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-400">
                      <PIcon className="h-3 w-3" /> {meta.label}
                    </div>
                  </div>
                  <div className="font-mono text-[10px] text-gray-500 mb-2">
                    {step.goal}
                  </div>
                  <div className="border border-[#1a1a1a] bg-black/60 p-3 font-mono text-xs text-gray-100 whitespace-pre-wrap leading-relaxed">
                    {step.caption || <span className="text-gray-500">— empty —</span>}
                  </div>
                  {step.hashtags && (
                    <div className="mt-1 font-mono text-[10px] text-[#FF4500]">{step.hashtags}</div>
                  )}
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button
                      data-testid={`rollout-copy-${step.id}`}
                      onClick={() => copy(step.hashtags ? `${step.caption}\n\n${step.hashtags}` : step.caption)}
                      disabled={!step.caption}
                      className="border border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-[10px] uppercase tracking-widest h-9 flex items-center justify-center gap-1.5 disabled:opacity-30"
                    >
                      <Copy className="h-3 w-3" /> Copy caption
                    </button>
                    <button
                      data-testid={`rollout-open-${step.id}`}
                      onClick={() => copyOpen(step)}
                      disabled={!step.caption}
                      className="bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-[10px] uppercase tracking-widest h-9 flex items-center justify-center gap-1.5 disabled:opacity-30"
                    >
                      <ExternalLink className="h-3 w-3" /> Copy + Open {meta.label}
                    </button>
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <div className="mt-6 font-mono text-[10px] text-gray-500 flex items-center gap-2">
          <Sparkles className="h-3 w-3" />
          Change the release date above and hit Regenerate to reshape every post's schedule.
        </div>
      </DialogContent>
    </Dialog>
  );
}
